import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Form,
  Input,
  InputGroup,
  InputGroupText,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";
import { requestJson } from "../../../../helpers/api";

type MapeoRegistro = {
  id?: number;
  monchisSucursal: string;
  fuente?: string | null;
  idsucursal?: number | null;
  sucursalNombre?: string | null;
  creadoPorId?: number | null;
  actualizadoPorId?: number | null;
  creadoPorNombre?: string | null;
  actualizadoPorNombre?: string | null;
  creadoEn?: string | null;
  actualizadoEn?: string | null;
};

type ConfigResponse = {
  ok: boolean;
  mapeos: MapeoRegistro[];
  sucursalesMonchis: string[];
  pendientes: string[];
};

type EmpresaOption = {
  fuente: string;
  empresaCodigo?: string | null;
  empresaNombre?: string | null;
  sucursales: Array<{ idsucursal: number; nombre: string }>;
};

type OpcionesResponse = {
  ok: boolean;
  empresas: EmpresaOption[];
};

type RowState = {
  monchisSucursal: string;
  id?: number;
  fuente: string;
  idsucursal: string;
  sucursalNombre?: string | null;
  creadoPorNombre?: string | null;
  actualizadoPorNombre?: string | null;
  creadoEn?: string | null;
  actualizadoEn?: string | null;
  creadoPorId?: number | null;
  actualizadoPorId?: number | null;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const MonchisSucursalMappingModal = ({ isOpen, onClose }: Props) => {
  const [rows, setRows] = useState<RowState[]>([]);
  const [empresas, setEmpresas] = useState<EmpresaOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendientes, setPendientes] = useState<Set<string>>(new Set());
  const [filtro, setFiltro] = useState("");
  const [accionEnCurso, setAccionEnCurso] = useState<Record<string, boolean>>({});

  const cargarDatos = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    try {
      const [config, opciones] = await Promise.all([
        requestJson<ConfigResponse>(
          "/api/tesoreria/importaciones/monchis/mapeos-sucursales",
        ),
        requestJson<OpcionesResponse>(
          "/api/tesoreria/importaciones/monchis/mapeos-sucursales/opciones",
        ),
      ]);
      const nombres = (config.sucursalesMonchis ?? [])
        .slice()
        .sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" }));
      const mapeosPorNombre = new Map(
        (config.mapeos ?? []).map((registro) => [
          registro.monchisSucursal,
          registro,
        ]),
      );
      const nuevosRows = nombres.map<RowState>((nombre) => {
        const actual = mapeosPorNombre.get(nombre);
        return {
          monchisSucursal: nombre,
          id: actual?.id,
          fuente: actual?.fuente ?? "",
          idsucursal: actual?.idsucursal ? String(actual.idsucursal) : "",
          sucursalNombre: actual?.sucursalNombre ?? null,
          creadoPorNombre: actual?.creadoPorNombre ?? null,
          actualizadoPorNombre: actual?.actualizadoPorNombre ?? null,
          creadoEn: actual?.creadoEn ?? null,
          actualizadoEn: actual?.actualizadoEn ?? null,
          creadoPorId: actual?.creadoPorId ?? null,
          actualizadoPorId: actual?.actualizadoPorId ?? null,
        };
      });
      setRows(nuevosRows);
      setEmpresas(opciones.empresas ?? []);
      setPendientes(
        new Set((config.pendientes ?? []).map((valor) => valor.toLowerCase())),
      );
    } catch (err) {
      const mensaje =
        err instanceof Error
          ? err.message
          : "No se pudieron cargar los mapeos de sucursales.";
      setError(mensaje);
    } finally {
      setLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    void cargarDatos();
  }, [isOpen, cargarDatos]);

  const opcionesPorFuente = useMemo(() => {
    const mapa = new Map<string, EmpresaOption>();
    empresas.forEach((empresa) => {
      mapa.set(empresa.fuente.toUpperCase(), empresa);
    });
    return mapa;
  }, [empresas]);

  const actualizarRow = (monchisSucursal: string, cambios: Partial<RowState>) => {
    setRows((prev) =>
      prev.map((row) =>
        row.monchisSucursal === monchisSucursal ? { ...row, ...cambios } : row,
      ),
    );
  };

  const manejarCambioFuente = (row: RowState, valor: string) => {
    actualizarRow(row.monchisSucursal, {
      fuente: valor,
      idsucursal: "",
    });
  };

  const manejarCambioSucursal = (row: RowState, valor: string) => {
    const empresa = opcionesPorFuente.get((row.fuente ?? "").toUpperCase());
    const sucursalNombre = empresa?.sucursales.find(
      (sucursal) => String(sucursal.idsucursal) === valor,
    )?.nombre;
    actualizarRow(row.monchisSucursal, {
      idsucursal: valor,
      sucursalNombre,
    });
  };

  const setRowAccion = (clave: string, estado: boolean) => {
    setAccionEnCurso((prev) => ({ ...prev, [clave]: estado }));
  };

  const guardarRow = async (row: RowState) => {
    if (!row.fuente || !row.idsucursal) {
      setError("Seleccioná una empresa y sucursal antes de guardar.");
      return;
    }
    const clave = row.monchisSucursal;
    setRowAccion(clave, true);
    setError(null);
    try {
      const respuesta = await requestJson<{ ok: boolean; registro: MapeoRegistro }>(
        "/api/tesoreria/importaciones/monchis/mapeos-sucursales",
        {
          method: "POST",
          body: JSON.stringify({
            id: row.id,
            monchisSucursal: row.monchisSucursal,
            fuente: row.fuente,
            idsucursal: Number(row.idsucursal),
          }),
        },
      );
      const registro = respuesta.registro;
      actualizarRow(row.monchisSucursal, {
        id: registro.id,
        fuente: registro.fuente ?? row.fuente,
        idsucursal: registro.idsucursal ? String(registro.idsucursal) : row.idsucursal,
        sucursalNombre: registro.sucursalNombre ?? row.sucursalNombre,
        creadoPorNombre: registro.creadoPorNombre ?? row.creadoPorNombre,
        actualizadoPorNombre: registro.actualizadoPorNombre ?? row.actualizadoPorNombre,
        creadoEn: registro.creadoEn ?? row.creadoEn,
        actualizadoEn: registro.actualizadoEn ?? row.actualizadoEn,
        creadoPorId: registro.creadoPorId ?? row.creadoPorId,
        actualizadoPorId: registro.actualizadoPorId ?? row.actualizadoPorId,
      });
      setPendientes((prev) => {
        const copia = new Set(prev);
        copia.delete(row.monchisSucursal.toLowerCase());
        return copia;
      });
    } catch (err) {
      const mensaje =
        err instanceof Error
          ? err.message
          : "No se pudo guardar el mapeo seleccionado.";
      setError(mensaje);
    } finally {
      setRowAccion(clave, false);
    }
  };

  const eliminarRow = async (row: RowState) => {
    if (!row.id) {
      actualizarRow(row.monchisSucursal, {
        fuente: "",
        idsucursal: "",
        sucursalNombre: undefined,
      });
      setPendientes((prev) => {
        const copia = new Set(prev);
        copia.add(row.monchisSucursal.toLowerCase());
        return copia;
      });
      return;
    }
    const clave = row.monchisSucursal;
    setRowAccion(clave, true);
    setError(null);
    try {
      await requestJson<{ ok: boolean }>(
        `/api/tesoreria/importaciones/monchis/mapeos-sucursales/${row.id}`,
        { method: "DELETE" },
      );
      actualizarRow(row.monchisSucursal, {
        id: undefined,
        fuente: "",
        idsucursal: "",
        sucursalNombre: undefined,
        creadoPorNombre: undefined,
        actualizadoPorNombre: undefined,
        creadoEn: undefined,
        actualizadoEn: undefined,
        creadoPorId: undefined,
        actualizadoPorId: undefined,
      });
      setPendientes((prev) => {
        const copia = new Set(prev);
        copia.add(row.monchisSucursal.toLowerCase());
        return copia;
      });
    } catch (err) {
      const mensaje =
        err instanceof Error
          ? err.message
          : "No se pudo eliminar el mapeo seleccionado.";
      setError(mensaje);
    } finally {
      setRowAccion(clave, false);
    }
  };

  const filasFiltradas = useMemo(() => {
    const criterio = filtro.trim().toLowerCase();
    if (!criterio) return rows;
    return rows.filter((row) =>
      row.monchisSucursal.toLowerCase().includes(criterio),
    );
  }, [rows, filtro]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl">
      <ModalHeader toggle={onClose}>Mapeo de sucursales de Monchis</ModalHeader>
      <ModalBody>
        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <Form onSubmit={(event) => event.preventDefault()} className="flex-grow-1">
            <InputGroup>
              <InputGroupText>
                <i className="ri-search-line" />
              </InputGroupText>
              <Input
                placeholder="Buscar sucursal de Monchis"
                value={filtro}
                onChange={(event) => setFiltro(event.target.value)}
                disabled={loading}
              />
            </InputGroup>
          </Form>
          <Button color="light" onClick={() => cargarDatos()} disabled={loading}>
            {loading ? <Spinner size="sm" /> : <i className="ri-refresh-line" />} Actualizar
          </Button>
        </div>
        {loading ? (
          <div className="text-center py-5">
            <Spinner size="sm" className="me-2" /> Cargando información…
          </div>
        ) : filasFiltradas.length === 0 ? (
          <div className="text-center text-muted py-4">
            No hay sucursales importadas desde Monchis todavía.
          </div>
        ) : (
          <div className="table-responsive">
            <Table className="align-middle table-nowrap">
              <thead className="table-light">
                <tr>
                  <th style={{ minWidth: 220 }}>Sucursal en Monchis</th>
                  <th style={{ minWidth: 200 }}>Empresa / Fuente</th>
                  <th style={{ minWidth: 220 }}>Sucursal Firebird</th>
                  <th>Estado</th>
                  <th style={{ minWidth: 220 }}>Auditoría</th>
                  <th style={{ width: 180 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filasFiltradas.map((row) => {
                  const fuenteClave = row.fuente?.toUpperCase() ?? "";
                  const empresa = opcionesPorFuente.get(fuenteClave);
                  const accionesDeshabilitadas = Boolean(accionEnCurso[row.monchisSucursal]);
                  const esPendiente = pendientes.has(
                    row.monchisSucursal.toLowerCase(),
                  );
                  return (
                    <tr key={row.monchisSucursal}>
                      <td>
                        <div className="fw-semibold">{row.monchisSucursal}</div>
                        {esPendiente && (
                          <Badge color="warning" pill className="mt-1">
                            Pendiente de mapeo
                          </Badge>
                        )}
                      </td>
                      <td>
                        <Input
                          type="select"
                          value={row.fuente}
                          onChange={(event) => manejarCambioFuente(row, event.target.value)}
                          disabled={accionesDeshabilitadas || empresas.length === 0}
                        >
                          <option value="">Seleccioná una empresa</option>
                          {empresas.map((item) => (
                            <option key={item.fuente} value={item.fuente}>
                              {item.empresaNombre || item.empresaCodigo || item.fuente}
                            </option>
                          ))}
                        </Input>
                      </td>
                      <td>
                        <Input
                          type="select"
                          value={row.idsucursal}
                          onChange={(event) => manejarCambioSucursal(row, event.target.value)}
                          disabled={accionesDeshabilitadas || !row.fuente}
                        >
                          <option value="">Seleccioná una sucursal</option>
                          {row.fuente &&
                            (empresa?.sucursales ?? []).map((sucursal) => (
                              <option key={sucursal.idsucursal} value={sucursal.idsucursal}>
                                {sucursal.nombre} (#{sucursal.idsucursal})
                              </option>
                            ))}
                        </Input>
                      </td>
                      <td>
                        {row.id && row.sucursalNombre ? (
                          <span className="text-success">
                            {row.sucursalNombre} (#{row.idsucursal})
                          </span>
                        ) : (
                          <span className="text-muted">Sin asignar</span>
                        )}
                      </td>
                      <td>
                        <div className="fw-semibold">
                          {row.actualizadoPorNombre ||
                            (row.actualizadoPorId
                              ? `Usuario #${row.actualizadoPorId}`
                              : null) ||
                            row.creadoPorNombre ||
                            (row.creadoPorId ? `Usuario #${row.creadoPorId}` : null) ||
                            "Sin registro"}
                        </div>
                        <div className="text-muted small">
                          {row.actualizadoEn
                            ? new Date(row.actualizadoEn).toLocaleString("es-PY")
                            : row.creadoEn
                              ? new Date(row.creadoEn).toLocaleString("es-PY")
                              : "Sin fecha"}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            color="success"
                            size="sm"
                            onClick={() => guardarRow(row)}
                            disabled={accionesDeshabilitadas || !row.fuente || !row.idsucursal}
                          >
                            {accionEnCurso[row.monchisSucursal] ? (
                              <Spinner size="sm" />
                            ) : row.id ? (
                              "Actualizar"
                            ) : (
                              "Guardar"
                            )}
                          </Button>
                          <Button
                            color="link"
                            size="sm"
                            className="text-danger"
                            onClick={() => eliminarRow(row)}
                            disabled={accionesDeshabilitadas || (!row.id && !row.fuente && !row.idsucursal)}
                          >
                            Quitar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <div className="text-muted me-auto">
          Empresas disponibles: {empresas.length}. Sucursales de Monchis: {rows.length}.
        </div>
        <Button color="light" onClick={onClose}>
          Cerrar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default MonchisSucursalMappingModal;
